import React, { useState } from "react";
import Modal from "react-modal";
import * as theme from "../theme";

const styles = {
  modal: {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      zIndex: 1000,
    },
    content: {
      position: "absolute",
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "500px",
      height: "auto",
      backgroundColor: "#ffffff",
      border: "none",
      borderRadius: "12px",
      padding: "0",
      overflow: "hidden",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
    },
  },
  header: {
    backgroundColor: "#dc2626",
    color: "white",
    padding: "1.5rem",
    textAlign: "center",
  },
  title: {
    margin: "0",
    fontSize: "1.25rem",
    fontWeight: "600",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  icon: {
    fontSize: "2rem",
    marginBottom: "0.5rem",
  },
  body: {
    padding: "2rem",
  },
  warningText: {
    fontSize: "1rem",
    lineHeight: "1.6",
    color: "#374151",
    marginBottom: "1.5rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  strongText: {
    fontWeight: "600",
    color: "#dc2626",
  },
  inputSection: {
    marginBottom: "1.5rem",
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "0.5rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  input: {
    width: "100%",
    padding: "0.75rem",
    border: "2px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "1rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    outline: "none",
    transition: "border-color 0.2s",
  },
  inputFocus: {
    borderColor: "#dc2626",
    boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.1)",
  },
  inputError: {
    borderColor: "#dc2626",
  },
  buttonGroup: {
    display: "flex",
    gap: "1rem",
    justifyContent: "flex-end",
    marginTop: "1.5rem",
  },
  button: {
    padding: "0.75rem 1.5rem",
    borderRadius: "6px",
    fontSize: "0.875rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s",
    border: "none",
    outline: "none",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  cancelButton: {
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
  },
  cancelButtonHover: {
    backgroundColor: "#e5e7eb",
  },
  deleteButton: {
    backgroundColor: "#dc2626",
    color: "white",
  },
  deleteButtonHover: {
    backgroundColor: "#b91c1c",
  },
  deleteButtonDisabled: {
    backgroundColor: "#9ca3af",
    cursor: "not-allowed",
  },
  loadingSpinner: {
    display: "inline-block",
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginRight: "0.5rem",
  },
};

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
  title = "Delete SAML Connection",
  message = "Are you sure you want to delete this SAML connection? This action cannot be undone and will disable single sign-on for your organization.",
  confirmationText = "delete",
  itemName = "SAML connection",
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  const isConfirmationValid = inputValue.toLowerCase() === confirmationText.toLowerCase();

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleConfirm = () => {
    if (isConfirmationValid && !isDeleting) {
      onConfirm();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isConfirmationValid && !isDeleting) {
      handleConfirm();
    }
  };

  const handleClose = () => {
    setInputValue("");
    onClose();
  };

  // Reset input when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setInputValue("");
    }
  }, [isOpen]);

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleClose}
        style={styles.modal}
        ariaHideApp={false}
        shouldCloseOnOverlayClick={!isDeleting}
        shouldCloseOnEsc={!isDeleting}
      >
        <div style={styles.header}>
          <div style={styles.icon}>⚠️</div>
          <h2 style={styles.title}>{title}</h2>
        </div>
        
        <div style={styles.body}>
          <p style={styles.warningText}>
            {message}
          </p>
          
          <p style={styles.warningText}>
            <span style={styles.strongText}>This action cannot be undone.</span> Your team members will no longer be able to sign in using SSO until you configure a new connection.
          </p>
          
          <div style={styles.inputSection}>
            <label style={styles.label}>
              To confirm deletion, type <strong>"{confirmationText}"</strong> below:
            </label>
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              disabled={isDeleting}
              placeholder={`Type "${confirmationText}" to confirm`}
              style={{
                ...styles.input,
                ...(isInputFocused ? styles.inputFocus : {}),
                ...(!isConfirmationValid && inputValue ? styles.inputError : {}),
              }}
            />
          </div>
          
          <div style={styles.buttonGroup}>
            <button
              onClick={handleClose}
              disabled={isDeleting}
              style={{
                ...styles.button,
                ...styles.cancelButton,
              }}
              onMouseEnter={(e) => !isDeleting && (e.target.style.backgroundColor = styles.cancelButtonHover.backgroundColor)}
              onMouseLeave={(e) => !isDeleting && (e.target.style.backgroundColor = styles.cancelButton.backgroundColor)}
            >
              Cancel
            </button>
            
            <button
              onClick={handleConfirm}
              disabled={!isConfirmationValid || isDeleting}
              style={{
                ...styles.button,
                ...styles.deleteButton,
                ...(!isConfirmationValid || isDeleting ? styles.deleteButtonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (isConfirmationValid && !isDeleting) {
                  e.target.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (isConfirmationValid && !isDeleting) {
                  e.target.style.backgroundColor = styles.deleteButton.backgroundColor;
                }
              }}
            >
              {isDeleting ? (
                <>
                  <div style={styles.loadingSpinner} />
                  Deleting...
                </>
              ) : (
                `Delete ${itemName}`
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DeleteConfirmationModal;